import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  fetchVapidPublicKey,
  registerPushSubscription,
  unregisterPushSubscription,
} from '../api/notifications'
import { updatePreferences, usePreferences } from '../api/preferences'

export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'unconfigured'
  | 'default'
  | 'denied'
  | 'granted-unsubscribed'
  | 'subscribed'

export type PushPlatform = 'ios' | 'android' | 'desktop'

/** Remembers the endpoint we last registered so a rotated one re-syncs. */
const ENDPOINT_KEY = 'todux.push.endpoint'

function readStoredEndpoint(): string | null {
  try {
    return localStorage.getItem(ENDPOINT_KEY)
  } catch {
    return null
  }
}

function storeEndpoint(endpoint: string | null): void {
  try {
    if (endpoint) localStorage.setItem(ENDPOINT_KEY, endpoint)
    else localStorage.removeItem(ENDPOINT_KEY)
  } catch {
    /* private mode — the server upsert is idempotent, so losing this is harmless */
  }
}

export function detectPlatform(): PushPlatform {
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'desktop'
}

/** iOS only delivers push to a home-screen install, never to a Safari tab. */
function isStandalone(): boolean {
  const navAny = navigator as Navigator & { standalone?: boolean }
  if (navAny.standalone) return true
  return window.matchMedia?.('(display-mode: standalone)').matches ?? false
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

function supportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export interface UsePushNotifications {
  /** What the browser/service-worker layer is doing. */
  state: PushState
  /**
   * What the On/Off control should show. The browser having a subscription is
   * not enough: the dispatcher checks the stored preference at send time, so a
   * user who turned notifications off must not see "On" just because this
   * device still holds a subscription.
   */
  enabled: boolean
  platform: PushPlatform
  deviceCount: number
  isBusy: boolean
  error: string | null
  subscribe(): Promise<void>
  unsubscribe(): Promise<void>
  recheck(): Promise<void>
}

export function usePushNotifications(): UsePushNotifications {
  const qc = useQueryClient()
  const { data: prefs } = usePreferences()
  const [state, setState] = useState<PushState>(() => (supportsPush() ? 'default' : 'unsupported'))
  const [deviceCount, setDeviceCount] = useState(0)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const platform = supportsPush() || typeof navigator !== 'undefined' ? detectPlatform() : 'desktop'

  const doSubscribe = useCallback(async (): Promise<void> => {
    const { public_key: vapid } = await fetchVapidPublicKey()
    const registration = await navigator.serviceWorker.ready

    let sub = await registration.pushManager.getSubscription()
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid) as BufferSource,
      })
    }

    const json = sub.toJSON()
    await registerPushSubscription({
      endpoint: sub.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      user_agent: navigator.userAgent,
      timezone: browserTimezone(),
    })
    storeEndpoint(sub.endpoint)
    setState('subscribed')
    setDeviceCount((n) => Math.max(n, 1))
    void qc.invalidateQueries({ queryKey: ['notifications', 'status'] })
  }, [qc])

  const setServerEnabled = useCallback(
    async (enabled: boolean): Promise<void> => {
      await updatePreferences({ notifications_enabled: enabled })
      await qc.invalidateQueries({ queryKey: ['preferences'] })
    },
    [qc]
  )

  const subscribe = useCallback(async (): Promise<void> => {
    if (!supportsPush()) return
    setIsBusy(true)
    setError(null)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'default')
        return
      }
      await doSubscribe()
      await setServerEnabled(true)
    } catch (e) {
      // A 503 here means the server has no VAPID keys; anything else is real.
      if (isUnconfigured(e)) setState('unconfigured')
      else setError('Could not enable notifications. Please try again.')
    } finally {
      setIsBusy(false)
    }
  }, [doSubscribe, setServerEnabled])

  const unsubscribe = useCallback(async (): Promise<void> => {
    setIsBusy(true)
    setError(null)
    try {
      const registration = await navigator.serviceWorker.ready
      const sub = await registration.pushManager.getSubscription()
      if (sub) {
        await unregisterPushSubscription(sub.endpoint).catch(() => {})
        await sub.unsubscribe().catch(() => {})
      }
      storeEndpoint(null)
      await setServerEnabled(false)
      setState(Notification.permission === 'granted' ? 'granted-unsubscribed' : 'default')
      setDeviceCount(0)
      void qc.invalidateQueries({ queryKey: ['notifications', 'status'] })
    } catch {
      setError('Could not turn notifications off. Please try again.')
    } finally {
      setIsBusy(false)
    }
  }, [qc, setServerEnabled])

  // recheck exists because a browser will not re-prompt after Block: the user
  // has to unblock in browser settings, and this is how the UI notices.
  const recheck = useCallback(async (): Promise<void> => {
    if (!supportsPush()) return
    if (Notification.permission === 'granted') {
      await subscribe()
      return
    }
    setState(Notification.permission === 'denied' ? 'denied' : 'default')
  }, [subscribe])

  // Resolve the true state on mount, and silently restore a subscription the
  // browser still has permission for — that is what keeps an existing install
  // working after the service worker is first deployed. Re-subscribing is
  // gated on the stored preference: a user who turned notifications off would
  // otherwise get them back on every reload.
  const prefsEnabled = prefs?.notifications_enabled
  useEffect(() => {
    if (!supportsPush()) {
      setState('unsupported')
      return
    }
    if (detectPlatform() === 'ios' && !isStandalone()) {
      setState('needs-install')
      return
    }

    let cancelled = false
    void (async () => {
      if (Notification.permission === 'denied') {
        setState('denied')
        return
      }
      if (Notification.permission !== 'granted') {
        setState('default')
        return
      }

      try {
        const registration = await navigator.serviceWorker.ready
        const sub = await registration.pushManager.getSubscription()
        if (cancelled) return

        if (prefsEnabled === false) {
          setState(sub ? 'subscribed' : 'granted-unsubscribed')
          return
        }
        // Preferences not loaded yet — wait for them rather than guessing.
        if (prefsEnabled === undefined) return

        if (!sub || sub.endpoint !== readStoredEndpoint()) {
          await doSubscribe()
          return
        }
        setState('subscribed')
      } catch (e) {
        if (!cancelled) setState(isUnconfigured(e) ? 'unconfigured' : 'granted-unsubscribed')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [doSubscribe, prefsEnabled])

  // Flip the UI the moment the user unblocks notifications in browser settings.
  useEffect(() => {
    if (!supportsPush() || !navigator.permissions?.query) return
    let status: PermissionStatus | null = null
    const onChange = () => void recheck()
    void navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then((s) => {
        status = s
        s.addEventListener('change', onChange)
      })
      .catch(() => {})
    return () => status?.removeEventListener('change', onChange)
  }, [recheck])

  return {
    state,
    enabled: prefsEnabled === true && state === 'subscribed',
    platform,
    deviceCount,
    isBusy,
    error,
    subscribe,
    unsubscribe,
    recheck,
  }
}

function isUnconfigured(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'error' in e &&
    (e as { error?: { code?: string } }).error?.code === 'push_unconfigured'
  )
}
